import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { ensureCsrfToken } from "../../../api/client";
import { AuthContext } from "../../../contexts/AuthContext";

const PhoneNumberPromptModal = () => {
  const { t } = useTranslation();
  const { user, refreshAuth, setUser } = useContext(AuthContext);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const open = Boolean(user?.needsPhonePrompt);
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setPhoneNumber(user?.phoneNumber || "");
      setError("");
    }
    prevOpenRef.current = open;
  }, [open, user?.phoneNumber]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!user?.id || busy) return;
      const trimmed = String(phoneNumber || "").trim();
      if (!trimmed) {
        setError(t("phonePrompt.required"));
        return;
      }
      setBusy(true);
      setError("");
      try {
        await ensureCsrfToken();
        const res = await api.patch("/api/auth/user/me", { phoneNumber: trimmed });
        if (res.data?.ok && res.data?.data?.user) {
          setUser(res.data.data.user);
          await refreshAuth();
          return;
        }
        setError(t("phonePrompt.submitError"));
      } catch (err) {
        setError(
          err?.response?.data?.message || err?.message || t("phonePrompt.submitError")
        );
      } finally {
        setBusy(false);
      }
    },
    [user?.id, busy, phoneNumber, refreshAuth, setUser, t]
  );

  if (!open || !user) return null;

  return (
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="phone-prompt-title"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md rounded-2xl bg-white shadow-xl p-6 sm:p-8"
      >
        <div className="text-center mb-6">
          <h2 id="phone-prompt-title" className="text-xl font-bold text-gray-900 mb-2">
            {t("phonePrompt.title")}
          </h2>
          <p className="text-gray-600 text-sm leading-relaxed">{t("phonePrompt.body")}</p>
        </div>

        <label htmlFor="phone-prompt-input" className="block font-semibold text-gray-700 mb-2">
          {t("phonePrompt.label")}
        </label>
        <input
          id="phone-prompt-input"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          autoFocus
          required
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          placeholder={t("phonePrompt.placeholder")}
          disabled={busy}
          className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all disabled:opacity-60"
        />

        {error ? (
          <p className="mt-3 text-sm text-red-600 text-center" role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={busy}
          className="mt-5 w-full py-3 px-4 rounded-xl bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold transition-colors"
        >
          {busy ? t("phonePrompt.saving") : t("phonePrompt.continue")}
        </button>

        <p className="mt-4 text-xs text-gray-500 text-center leading-relaxed">
          {t("phonePrompt.hint")}
        </p>
      </form>
    </div>
  );
};

export default PhoneNumberPromptModal;
